import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Paper, useMediaQuery, useTheme } from '@mui/material';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { RenderCTA, renderCTAES } from '../FormCTA/FormCTA.helpers';
import FurtherAssistanceContact from '../FurtherAssistanceContact';
import { handleTabSelection, makeTestIdForTabLink, NodeIndexIndicator, TabLabelText } from './TabbedForm.helpers';
import TabPanel from './TabPanel';

import AppointmentReview, { AppointmentSubmitHandle } from '@src/components/AppointmentReview';
import Confirmation from '@src/components/Confirmation';
import DateAndTime from '@src/components/DateAndTime';
import {
	AdditionalPatientInformationForm,
	FullPatientInformationForm,
	GuarantorInformationForm,
	PatientInformationForm,
} from '@src/components/forms';
import { useAppointmentStore, useFormSectionStore, usePatientInformationFormStore } from '@src/store';
import { slugify } from '@src/utils/slugify';
import { trimTimeZoneOffset } from '@src/utils/trimTimeZoneOffset';
import { SuccessCard } from 'types/generated';

interface TabbedFormProps {
	successCardsCollection: { items: Array<SuccessCard> };
	specialFlow?: boolean;
}

const TabbedForm = ({ successCardsCollection, specialFlow }: TabbedFormProps) => {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
	const { t } = useTranslation('tabbedForm');
	const router = useRouter();
	const { locale } = router;

	const { availableFormNodes, formNodes, lockForm } = useFormSectionStore((store) => store);
	const { firstName, lastName } = usePatientInformationFormStore((store) => store);
	const appointmentSlot = useAppointmentStore((store) => store.appointmentSlot);

	const activeSectionIndex = useFormSectionStore((store) => store.activeSectionIndex);

	const childRef = useRef<AppointmentSubmitHandle>(null);

	const determineStepSubtext = (stepName: string) => {
		if (stepName === 'Patient Information') {
			return `${firstName} ${lastName}`;
		}

		if (stepName === 'Date & Time' && appointmentSlot?.time) {
			const formattedAppointmentDate = new Intl.DateTimeFormat(locale, {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			}).format(new Date(appointmentSlot?.time));

			const appointmentStartTime = new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(
				new Date(trimTimeZoneOffset(appointmentSlot?.time) ?? '')
			);

			return `${formattedAppointmentDate} - ${appointmentStartTime}`;
		}

		return null;
	};

	useEffect(() => {
		window.scrollTo(0, 0);
		const customGlobalThis = global as unknown as Record<string, any>;
		if (customGlobalThis?.Invoca) {
			customGlobalThis.Invoca?.PNAPI?.run();
		}
	}, [activeSectionIndex]);

	const confirmAppointment = () => {
		childRef?.current?.handleSubmit();
	};

	return (
		<>
			<Box pb={3} px={0}>
				<Container sx={{ px: 0, pb: 2 }}>
					<Grid container spacing={2}>
						{!isSmallScreen && (
							<Grid item xs={3} mt={4}>
								<Paper elevation={0} variant="outlined">
									{locale === 'en-US' && (
										<Tabs
											orientation="vertical"
											onChange={handleTabSelection}
											value={activeSectionIndex}
											sx={{
												'.MuiTabs-indicator': { left: 0, bgcolor: 'secondary.main', width: 4 },
											}}
										>
											{availableFormNodes().map((node, index) => {
												const sectionLocked = lockForm && node.name !== 'Confirmation';
												const priorNodeIncomplete =
													!!node.index && !availableFormNodes()[index - 1].completed;

												return (
													<Tab
														sx={{
															display: 'flex',
															justifyContent: 'start',
															paddingLeft: { sm: 1, md: 2 },
															...(activeSectionIndex === node.index && {
																bgcolor: 'backgrounds.light',
															}),
														}}
														key={node.name}
														label={
															<TabLabelText
																label={node.name}
																subLabel={determineStepSubtext(node.name) || ''}
																active={activeSectionIndex === node.index}
																disabled={sectionLocked || priorNodeIncomplete}
															/>
														}
														value={node.index}
														disabled={sectionLocked || priorNodeIncomplete}
														data-test-id={makeTestIdForTabLink(node.name)}
														id={`vertical-tab-${node.index}`}
														icon={
															<Typography color="secondary.main" variant="overline">
																{node.completed ||
																(node.name === 'Confirmation' && lockForm) ? (
																	<CheckCircleIcon
																		role="status"
																		fontSize="small"
																		sx={{
																			visibility:
																				node.completed || lockForm
																					? 'visible'
																					: 'hidden',
																			opacity: sectionLocked ? 0.5 : 1,
																		}}
																	/>
																) : (
																	<NodeIndexIndicator
																		index={index + 1}
																		active={activeSectionIndex === node.index}
																	/>
																)}
															</Typography>
														}
														iconPosition="start"
													/>
												);
											})}
										</Tabs>
									)}
									{locale === 'es-US' && (
										<Tabs
											orientation="vertical"
											onChange={handleTabSelection}
											value={activeSectionIndex}
											sx={{
												'.MuiTabs-indicator': { left: 0, bgcolor: 'secondary.main', width: 4 },
											}}
										>
											{availableFormNodes().map((node, index) => {
												const sectionLocked = lockForm && node.name !== 'Confirmation';
												const priorNodeIncomplete =
													!!node.index && !availableFormNodes()[index - 1].completed;
												return (
													<Tab
														sx={{
															display: 'flex',
															justifyContent: 'start',
															paddingLeft: { sm: 1, md: 2 },
															...(activeSectionIndex === node.index && {
																bgcolor: 'backgrounds.light',
															}),
														}}
														key={node.name}
														label={
															<TabLabelText
																label={node.nameES}
																subLabel={determineStepSubtext(node.name) || ''}
																active={activeSectionIndex === node.index}
																disabled={sectionLocked || priorNodeIncomplete}
															/>
														}
														value={node.index}
														disabled={sectionLocked || priorNodeIncomplete}
														data-test-id={makeTestIdForTabLink(node.name)}
														id={`vertical-tab-${node.index}`}
														icon={
															<Typography color="secondary.main" variant="overline">
																{node.completed ||
																(node.name === 'Confirmation' && lockForm) ? (
																	<CheckCircleIcon
																		role="status"
																		fontSize="small"
																		sx={{
																			visibility:
																				node.completed || lockForm
																					? 'visible'
																					: 'hidden',
																			opacity: sectionLocked ? 0.5 : 1,
																		}}
																	/>
																) : (
																	<NodeIndexIndicator
																		index={index + 1}
																		active={activeSectionIndex === node.index}
																	/>
																)}
															</Typography>
														}
														iconPosition="start"
													/>
												);
											})}
										</Tabs>
									)}
								</Paper>
							</Grid>
						)}

						<Grid
							item
							mt={isSmallScreen ? 0 : 4}
							xs={isSmallScreen ? 12 : 9}
							sx={{
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							<Paper elevation={0} variant={isSmallScreen ? 'elevation' : 'outlined'} sx={{ pb: 4 }}>
								{/* standard flow */}
								{!specialFlow && (
									<>
										<TabPanel tabHeader={t('patientInfo')} value={activeSectionIndex} index={0}>
											<PatientInformationForm formId={slugify(formNodes[0].headerTitle)} />
										</TabPanel>
										<TabPanel tabHeader={t('dateTime')} value={activeSectionIndex} index={1}>
											<DateAndTime />
										</TabPanel>
										<TabPanel tabHeader={t('additionalInfo')} value={activeSectionIndex} index={2}>
											<AdditionalPatientInformationForm
												formId={slugify(formNodes[2].headerTitle)}
											/>
										</TabPanel>
										{formNodes[3].available ? (
											<TabPanel
												tabHeader={t('guarantorInfo')}
												value={activeSectionIndex}
												index={3}
											>
												<GuarantorInformationForm formId={slugify(formNodes[3].headerTitle)} />
											</TabPanel>
										) : null}
										<TabPanel
											tabHeader={t('apptReview')}
											value={activeSectionIndex}
											index={4}
											removeHeaderMarginBottom
										>
											<AppointmentReview ref={childRef} />
										</TabPanel>
										<TabPanel
											tabHeader={`${t('confirm')} ${firstName}`}
											value={activeSectionIndex}
											index={5}
											appointmentScheduled
										>
											<Confirmation successCardsCollection={successCardsCollection} />
										</TabPanel>
									</>
								)}
								{/* motto flow */}
								{specialFlow && (
									<>
										<TabPanel
											tabHeader={t('dateTime')} //{formNodes[0].headerTitle}
											value={activeSectionIndex}
											index={0}
										>
											<DateAndTime />
										</TabPanel>
										<TabPanel
											tabHeader={t('patientInfo')} //{formNodes[1].headerTitle}
											value={activeSectionIndex}
											index={1}
										>
											<FullPatientInformationForm formId={slugify(formNodes[1].headerTitle)} />
										</TabPanel>
										{formNodes[2].available ? (
											<TabPanel
												tabHeader={t('guarantorInfo')}
												value={activeSectionIndex}
												index={2}
											>
												<GuarantorInformationForm formId={slugify(formNodes[2].headerTitle)} />
											</TabPanel>
										) : null}
										<TabPanel
											tabHeader={t('apptReview')}
											value={activeSectionIndex}
											index={3}
											removeHeaderMarginBottom
										>
											<AppointmentReview ref={childRef} />
										</TabPanel>
										<TabPanel
											tabHeader={`${t('confirm')} ${firstName}`}
											value={activeSectionIndex}
											index={4}
											appointmentScheduled
										>
											<Confirmation successCardsCollection={successCardsCollection} />
										</TabPanel>
									</>
								)}
							</Paper>
							<Box pt={2} pb={7.5}>
								<FurtherAssistanceContact />
							</Box>
						</Grid>
					</Grid>
				</Container>
			</Box>
			{locale === 'en-US' && RenderCTA(formNodes[activeSectionIndex].name, confirmAppointment)}
			{locale === 'es-US' && renderCTAES(formNodes[activeSectionIndex].name, confirmAppointment)}
		</>
	);
};

export default TabbedForm;
